<template>
  <v-app>
    <v-navigation-drawer
      v-if="$store.state.user.isAuth && !$route.meta.printView && !$route.meta.allowPublic"
      v-model="drawer"
      rail
    >
      <v-list>
        <v-list-item>
          <v-icon @click.stop="drawer = !drawer">mdi-menu</v-icon>
        </v-list-item>
      </v-list>
      <v-list density="compact">
        <v-list-item
          v-for="item in primaryNav"
          :key="item.to"
          :to="item.to"
          :prepend-icon="item.icon"
          :title="item.title"
        ></v-list-item>
      </v-list>
      <v-list density="compact" class="mt-auto">
        <v-list-item
          v-for="item in secondaryNav"
          :key="item.to"
          :to="item.to"
          :prepend-icon="item.icon"
          :title="item.title"
        ></v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar
      v-if="user !== null && !$route.meta.printView && !$route.meta.allowPublic"
      color="primary"
      theme="dark"
      class="elevation-0"
    >
      <v-app-bar-nav-icon @click.stop="drawer = !drawer" v-if="!drawer"></v-app-bar-nav-icon>
      <h1 class="text-h5 ml-5">
        {{ $store.state.pageTitle }}
      </h1>
      <v-spacer></v-spacer>
      <!-- V3 splits V2's `fab`/`small`/`depressed` into `icon`, `size` and `variant`. -->
      <v-btn
        class="ml-4 mr-4"
        color="accent"
        icon
        size="small"
        variant="flat"
        @click="quoteAddDialog = !quoteAddDialog"
      >
        <v-icon>mdi-clipboard-text</v-icon>
      </v-btn>
    </v-app-bar>

    <v-main>
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" :key="$route.fullPath" />
        </transition>
      </router-view>
    </v-main>

    <!-- New Quote Dialog -->
    <v-dialog v-if="user !== null && !$route.meta.allowPublic" v-model="quoteAddDialog" width="500">
      <QuoteAdd @cancel="quoteAddDialog = false" @complete="quoteAddDialog = false"></QuoteAdd>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      color="accent"
      v-model="$store.state.snackbar"
      :timeout="$store.state.snackbarTimeout"
      location="top"
      class="py-0"
      >{{ $store.state.snackbarText }}
      <template v-slot:actions>
        <v-icon :size="20" @click="$store.commit('closeSnackbar')" class="mr-1">mdi-close </v-icon>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import { collection, doc, onSnapshot } from 'firebase/firestore'

import QuoteAdd from '@/components/QuoteAdd'
import { auth, db } from '@/js/db'

export default {
  name: 'Default',
  components: { QuoteAdd },
  data() {
    return {
      defaultRoute: this.$route.path,
      user: auth.currentUser,
      settingsDocID: 'mPPqOiiHvphomjk1qrI1',
      applicationVersion: 315,
      drawer: false,
      quoteAddDialog: false,
      googleMapsApiLoaded: false,
      // onSnapshot unsubscribe handles, released in beforeUnmount
      unsubs: [],

      primaryNavItems: [
        { to: '/', icon: 'mdi-view-dashboard', title: 'Dashboard' },
        { to: '/quotes', icon: 'mdi-clipboard-text', title: 'Quotes' },
        { to: '/invoiced', icon: 'mdi-credit-card-outline', title: 'Awaiting Payment' },
        { to: '/production', icon: 'mdi-wrench', title: 'Production' },
        { to: '/dispatched', icon: 'mdi-truck', title: 'Dispatched' },
        { to: '/samples', icon: 'mdi-view-column', title: 'Samples' },
        { to: '/offcuts', icon: 'mdi-shape', title: 'Offcuts' }
      ],
      secondaryNavItems: [
        { to: '/recuts', icon: 'mdi-repeat', title: 'Recuts' },
        { to: '/stock', icon: 'mdi-bookshelf', title: 'Stock' },
        { to: '/orders', icon: 'mdi-receipt-text', title: 'Orders', permission: 'orders' },
        { to: '/analytics', icon: 'mdi-chart-line', title: 'Analytics', permission: 'analytics' },
        { to: '/settings', icon: 'mdi-cog', title: 'Settings', permission: 'settings' },
        { to: '/user', icon: 'mdi-account', title: 'User Settings' }
      ]
    }
  },
  computed: {
    primaryNav() {
      return this.primaryNavItems.filter((item) => this.navAllowed(item))
    },
    secondaryNav() {
      return this.secondaryNavItems.filter((item) => this.navAllowed(item))
    }
  },
  mounted() {
    let placesKey
    if (window.location.hostname == 'mega.plykea.com') {
      placesKey = 'AIzaSyAv4aRIjITY5teVksdZiTZkJiYW2-AtkjU' //prod
    } else {
      placesKey = 'AIzaSyCIUq0Y4cXiT9oIsPN4JReKvOS1C5QQf58' //dev
    }

    const src = `https://maps.googleapis.com/maps/api/js?key=${placesKey}&loading=async&libraries=places`

    this.loadGoogleMapsApi(src)
  },
  created() {
    this.drawer = !this.$vuetify.display.mdAndDown

    if (this.$store.state.user.isAuth) this.startDataListeners()
  },
  watch: {
    '$store.state.user.isAuth'(isAuth) {
      if (isAuth) this.startDataListeners()
      else this.stopDataListeners()
    }
  },
  beforeUnmount() {
    this.stopDataListeners()
  },
  methods: {
    listen(label, ref, onNext) {
      this.unsubs.push(onSnapshot(ref, onNext, (err) => console.error(`[bootstrap] "${label}" listener failed:`, err)))
    },
    startDataListeners() {
      if (this.unsubs.length) return

      // Load in various settings
      this.listen('settings', doc(db, 'settings', this.settingsDocID), (snap) => {
        if (this.applicationVersion < snap.data().applicationVersion) {
          this.$store.commit('openSnackbar', {
            snackbarText: 'Your version of MegaAdmin is out of date. Please refresh your browser',
            timeout: -1
          })
        }
        this.$store.commit('muSettings', snap.data())
      })

      // Load Settings
      this.listen('pricing', doc(db, 'settings', 'pricing'), (snap) => {
        this.$store.commit('updatePricing', snap.data())
      })

      // Load Materials (live only — archived docs are inspected separately via MaterialSettings' Show archived toggle)
      this.listen('materials', collection(db, 'materials'), (snap) => {
        let materials = []
        for (const m of snap.docs) {
          let mat = m.data()
          if (mat.archived) continue
          mat.id = m.id
          if (mat.colours) {
            mat.colours.push({
              hex: '#F2F2E9',
              text: 'TBC',
              value: 'tbc'
            })
          }
          materials.push(mat)
        }
        this.$store.commit('updateMaterials', materials)
      })

      // Load worktops (live only — archived docs are inspected separately via WorktopSettings' Show archived toggle)
      this.listen('worktops', collection(db, 'worktops'), (snap) => {
        let worktops = []
        for (const w of snap.docs) {
          let wt = w.data()
          if (wt.archived) continue
          wt.id = w.id
          worktops.push(wt)
        }
        this.$store.commit('updateWorktops', worktops)
      })

      // Load Handles
      this.listen('handles', collection(db, 'handles'), (snap) => {
        let handles = []
        for (let h of snap.docs) {
          handles.push(h.data())
        }
        this.$store.commit('updateHandles', handles)
      })

      // Load Staff
      this.listen('staff', collection(db, 'staff'), (snap) => {
        let staff = []
        for (let s of snap.docs) {
          let d = s.data()
          d.id = s.id
          staff.push(d)
        }
        this.$store.commit('updateStaff', staff)
      })
    },
    stopDataListeners() {
      this.unsubs.forEach((unsub) => unsub())
      this.unsubs = []
    },
    navAllowed(item) {
      return !item.permission || !!this.$store.state.user.permissions?.[item.permission]
    },
    loadGoogleMapsApi(src) {
      if (this.googleMapsApiLoaded) return // Prevent duplicate loading

      const script = document.createElement('script')
      script.src = src
      script.async = true
      script.defer = true
      script.onload = () => {
        this.googleMapsApiLoaded = true
      }

      document.head.appendChild(script)
    }
  }
}
</script>

<style lang="scss">
.v-navigation-drawer__content {
  display: flex;
  flex-direction: column;
}
</style>
<style lang="scss" scoped>
h1 {
  font-weight: 500 !important;
}
</style>
